import React, { useEffect, useState } from 'react';
import { Customer } from '../../../shared/types';
import { supabase } from '../../../shared/services/supabase';
import { ContractUpload } from './ContractUpload';
import { VertragPanel } from '../../vertraege/components/VertragPanel';
import { InfoTooltip } from '../../../shared/components/InfoTooltip';

// Zwei-Lauf-Modell: Formular-Zustand einer Lohnschein-Versand-Konfiguration pro
// Lohnlauf. Liegt in den Modalen, wird als Prop hereingereicht und beim Speichern
// in die Tabelle customer_lohnschein_versand_configs geschrieben.
export interface LohnscheinLaufFormConfig {
  lohnlauf: 1 | 2;
  aktiv: boolean;
  modus: 'fest' | 'relativ';
  tag: number | null;
  verschiebung: string | null;
  monat_offset: number | null;
  bezug_werktage_offset: number | null;
}

export const defaultLohnscheinLauf = (lohnlauf: 1 | 2): LohnscheinLaufFormConfig => ({
  lohnlauf,
  aktiv: false,
  modus: 'fest',
  tag: null,
  verschiebung: null,
  monat_offset: 0,
  bezug_werktage_offset: null
});

interface CustomerFormProps {
  formData: Partial<Customer>;
  activeTab: 'stammdaten' | 'lohnlauf' | 'steuer_sozialversicherung' | 'rechnungsdaten';
  errors: Record<string, string>;
  onChange: (field: keyof Customer, value: any) => void;
  showContactFields?: boolean; // NEU: Kontaktfelder im Stammdaten-Tab anzeigen (Default: true für CreateCustomerModal)
  // Rechnungsdaten-Props (optional, nur im EditCustomerModal verwendet)
  customerId?: string;
  pricingValue?: number | null;
  onPricingChange?: (value: number | null) => void;
  // Stundensatz-Props (optional, nur im EditCustomerModal verwendet)
  stundensatzValue?: number | null;
  onStundensatzChange?: (value: number | null) => void;
  // Zwei-Lauf-Modell: Lohnschein-Versand-Konfiguration pro Lohnlauf (liegt im Modal).
  lohnscheinConfigs?: LohnscheinLaufFormConfig[];
  onLohnscheinConfigChange?: (lohnlauf: 1 | 2, patch: Partial<LohnscheinLaufFormConfig>) => void;
}

interface StaffMember {
  id: string;
  full_name: string;
  initials: string;
}

interface AuftraggeberOption {
  id: string;
  company: string;
}

interface CustomerGroupOption {
  id: string;
  name: string;
}

// Betriebsstättenfinanzamt für Steuer-ID-Anträge (Tabelle finanzaemter).
interface FinanzamtOption {
  id: string;
  name: string;
}

export const CustomerForm: React.FC<CustomerFormProps> = ({
  formData,
  activeTab,
  errors,
  onChange,
  showContactFields = true,
  customerId,
  pricingValue,
  onPricingChange,
  stundensatzValue,
  onStundensatzChange,
  lohnscheinConfigs,
  onLohnscheinConfigChange
}) => {
  const [availableStaff, setAvailableStaff] = useState<StaffMember[]>([]);
  const [availableAuftraggeber, setAvailableAuftraggeber] = useState<AuftraggeberOption[]>([]);
  const [availableGroups, setAvailableGroups] = useState<CustomerGroupOption[]>([]);
  const [availableFinanzaemter, setAvailableFinanzaemter] = useState<FinanzamtOption[]>([]);
  const [pricingDisplay, setPricingDisplay] = useState<string>('');
  const pricingDisplayRef = React.useRef<string>('');

  // Wrapper um setPricingDisplay, der auch den Ref aktualisiert
  const updatePricingDisplay = (value: string) => {
    pricingDisplayRef.current = value;
    setPricingDisplay(value);
  };

  // Sync pricingValue (number) → pricingDisplay (string) bei externem Update
  useEffect(() => {
    if (pricingValue !== null && pricingValue !== undefined) {
      // Nur überschreiben wenn der aktuelle Display-Wert nicht dem Zahlenwert entspricht
      // (verhindert Cursor-Sprünge während der Eingabe)
      const currentNumeric = parseFloat(pricingDisplayRef.current.replace(',', '.'));
      if (isNaN(currentNumeric) || currentNumeric !== pricingValue) {
        updatePricingDisplay(String(pricingValue).replace('.', ','));
      }
    } else if (pricingDisplayRef.current !== '') {
      updatePricingDisplay('');
    }
  }, [pricingValue]);

  // Stundensatz-Anzeige (analog zum Preis pro Arbeitnehmer)
  const [stundensatzDisplay, setStundensatzDisplay] = useState<string>('');
  const stundensatzDisplayRef = React.useRef<string>('');

  const updateStundensatzDisplay = (value: string) => {
    stundensatzDisplayRef.current = value;
    setStundensatzDisplay(value);
  };

  // Sync stundensatzValue (number) → stundensatzDisplay (string) bei externem Update
  useEffect(() => {
    if (stundensatzValue !== null && stundensatzValue !== undefined) {
      const currentNumeric = parseFloat(stundensatzDisplayRef.current.replace(',', '.'));
      if (isNaN(currentNumeric) || currentNumeric !== stundensatzValue) {
        updateStundensatzDisplay(String(stundensatzValue).replace('.', ','));
      }
    } else if (stundensatzDisplayRef.current !== '') {
      updateStundensatzDisplay('');
    }
  }, [stundensatzValue]);

  // Lade verfügbare Mitarbeiter
  useEffect(() => {
    const loadStaff = async () => {
      const { data, error } = await supabase
        .from('xtax_staff')
        .select('id, full_name, initials')
        .eq('is_active', true)
        .order('sort_order');
      
      if (!error && data) {
        setAvailableStaff(data);
      }
    };
    loadStaff();
  }, []);

  // Lade verfügbare Auftraggeber (Kunden mit ist_auftraggeber = true)
  useEffect(() => {
    const loadAuftraggeber = async () => {
      const { data, error } = await supabase
        .from('customers')
        .select('id, company')
        .eq('ist_auftraggeber', true)
        .eq('deleted', false)
        .order('company');
      
      if (!error && data) {
        setAvailableAuftraggeber(data);
      }
    };
    loadAuftraggeber();
  }, []);

  // Lade verfügbare Kundengruppen (für Umsatzanalyse)
  useEffect(() => {
    const loadGroups = async () => {
      const { data, error } = await supabase
        .from('customer_groups')
        .select('id, name')
        .order('name');
      
      if (!error && data) {
        setAvailableGroups(data);
      }
    };
    loadGroups();
  }, []);

  // Lade aktive Finanzämter (Betriebsstättenfinanzamt für Steuer-ID-Anträge)
  useEffect(() => {
    const loadFinanzaemter = async () => {
      const { data, error } = await supabase
        .from('finanzaemter')
        .select('id, name')
        .eq('aktiv', true)
        .order('name');

      if (!error && data) {
        setAvailableFinanzaemter(data);
      }
    };
    loadFinanzaemter();
  }, []);

  // OPPM-Standort: Konfiguration der OPPM-QR-Versand-Frist nur fuer Kunden der
  // Gruppe "OPPM" anzeigen (pro Standort aktivierbar).
  const isOppmStandort = React.useMemo(() => {
    const oppmGroup = availableGroups.find(g => g.name.trim().toUpperCase() === 'OPPM');
    return !!oppmGroup && formData.customer_group_id === oppmGroup.id;
  }, [availableGroups, formData.customer_group_id]);

  const renderField = (
    label: string,
    field: keyof Customer,
    type: 'text' | 'email' | 'number' | 'select' | 'checkbox' = 'text',
    options?: { value: string; label: string }[],
    placeholder?: string,
    helpText?: string
  ) => {
    const fieldError = errors[field];

    if (type === 'checkbox') {
      return (
        <label style={{ 
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          padding: '4px 0',
          cursor: 'pointer',
          userSelect: 'none'
        }}>
          <div style={{ position: 'relative' }}>
            <input
              type="checkbox"
              checked={formData[field] as boolean || false}
              onChange={(e) => onChange(field, e.target.checked)}
              style={{ 
                position: 'absolute',
                opacity: 0,
                width: '20px',
                height: '20px',
                cursor: 'pointer'
              }}
            />
            <div style={{
              width: '20px',
              height: '20px',
              borderRadius: '4px',
              border: `2px solid ${(formData[field] as boolean) ? '#3B82F6' : '#D1D5DB'}`,
              backgroundColor: (formData[field] as boolean) ? '#3B82F6' : 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease'
            }}>
              {(formData[field] as boolean) && (
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path 
                    d="M10 3L4.5 8.5L2 6" 
                    stroke="white" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </div>
          </div>
          <span style={{ 
            fontSize: '14px', 
            color: '#374151',
            fontWeight: '400'
          }}>
            {label}
          </span>
        </label>
      );
    }

    return (
      <div style={{ 
        display: 'flex',
        flexDirection: 'column',
        gap: '6px'
      }}>
        <label style={{ 
          fontSize: '14px', 
          fontWeight: '500', 
          color: '#374151' 
        }}>
          {label}
        </label>
        {type === 'select' ? (
          <select
            value={formData[field] as string || ''}
            onChange={(e) => onChange(field, e.target.value || null)}
            style={{
              padding: '8px 12px',
              border: fieldError ? '1px solid #EF4444' : '1px solid #D1D5DB',
              borderRadius: '6px',
              fontSize: '14px',
              color: '#111827',
              backgroundColor: 'white',
              outline: 'none',
              transition: 'border-color 0.2s'
            }}
          >
            <option value="">-- Bitte wählen --</option>
            {options?.map(opt => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        ) : (
          <input
            type={type}
            value={formData[field] as string || ''}
            onChange={(e) => {
              const value = type === 'number' 
                ? (e.target.value ? parseInt(e.target.value) : null)
                : e.target.value;
              onChange(field, value);
            }}
            placeholder={placeholder}
            style={{
              padding: '8px 12px',
              border: fieldError ? '1px solid #EF4444' : '1px solid #D1D5DB',
              borderRadius: '6px',
              fontSize: '14px',
              color: '#111827',
              backgroundColor: 'white',
              outline: 'none',
              transition: 'border-color 0.2s'
            }}
          />
        )}
        {helpText && !fieldError && (
          <p style={{ 
            fontSize: '12px', 
            color: '#6B7280',
            margin: 0 
          }}>
            {helpText}
          </p>
        )}
        {fieldError && (
          <p style={{ 
            fontSize: '12px', 
            color: '#EF4444',
            margin: 0 
          }}>
            {fieldError}
          </p>
        )}
      </div>
    );
  };

  // Rendert die Eingabefelder einer der drei Spezialfristen (Lohnschein-Versand,
  // OPPM-QR, Monatsübersicht-QR) inklusive Umschalter zwischen festem Tag und
  // relativem Lohnlauf-Bezug. Der Umschalter erscheint nur, wenn mindestens ein
  // Lohnlauf konfiguriert ist. Im Fest-Modus bleibt das bisherige Dreispalten-
  // Raster unverändert; im Relativ-Modus werden Anker, Werktage und Richtung
  // abgefragt. Beim Moduswechsel werden die Felder des jeweils anderen Modus
  // geleert, damit keine Mischzustände entstehen.
  const renderSpezialfristFelder = (
    prefix: 'lohnschein_versand' | 'oppm_qr_versand' | 'monatsuebersicht_qr_versand',
    tagHelpText: string
  ) => {
    const bezugField = `${prefix}_bezug_lohnlauf` as keyof Customer;
    const offsetField = `${prefix}_bezug_werktage_offset` as keyof Customer;
    const tagField = `${prefix}_tag` as keyof Customer;
    const verschiebungField = `${prefix}_verschiebung` as keyof Customer;
    const monatOffsetField = `${prefix}_monat_offset` as keyof Customer;

    const bezugValue = formData[bezugField] as number | null | undefined;
    const istRelativ = !!bezugValue;
    const offset = (formData[offsetField] as number | null | undefined) ?? 0;
    const betrag = Math.max(1, Math.abs(offset) || 1);
    const richtung: 'davor' | 'danach' | 'am_tag' = offset === 0 ? 'am_tag' : offset < 0 ? 'davor' : 'danach';

    const verfuegbar: Array<{ value: 1 | 2; label: string }> = [];
    if (formData.erster_lohnlauf_tag) verfuegbar.push({ value: 1, label: '1. Lohnlauf' });
    if (formData.zweiter_lohnlauf_tag) verfuegbar.push({ value: 2, label: '2. Lohnlauf' });

    const aktiviereRelativ = () => {
      const anker = verfuegbar[0]?.value ?? 1;
      onChange(bezugField, anker);
      onChange(offsetField, betrag);
      onChange(tagField, null);
      onChange(verschiebungField, null);
      onChange(monatOffsetField, null);
    };
    const aktiviereFest = () => {
      onChange(bezugField, null);
      onChange(offsetField, null);
    };
    const setzeOffset = (b: number, r: 'davor' | 'danach' | 'am_tag') => {
      if (r === 'am_tag') {
        onChange(offsetField, 0);
        return;
      }
      const betragValid = Math.max(1, b || 1);
      onChange(offsetField, r === 'danach' ? betragValid : -betragValid);
    };

    const feldStyle = {
      padding: '8px 12px',
      border: '1px solid #D1D5DB',
      borderRadius: '6px',
      fontSize: '14px',
      color: '#111827',
      backgroundColor: 'white',
      outline: 'none',
      transition: 'border-color 0.2s'
    } as const;
    const labelStyle = {
      fontSize: '14px',
      fontWeight: '500',
      color: '#374151'
    } as const;

    return (
      <>
        {verfuegbar.length > 0 && (
          <div style={{ marginBottom: '16px' }}>
            <label style={{ ...labelStyle, display: 'block', marginBottom: '8px' }}>
              Fristart
            </label>
            <div style={{ display: 'flex', gap: '16px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '14px', color: '#374151' }}>
                <input type="radio" checked={!istRelativ} onChange={aktiviereFest} />
                Fester Tag
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '14px', color: '#374151' }}>
                <input type="radio" checked={istRelativ} onChange={aktiviereRelativ} />
                Relativ zum Lohnlauf
              </label>
            </div>
          </div>
        )}

        {istRelativ ? (
          <div style={{ display: 'grid', gridTemplateColumns: richtung === 'am_tag' ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)', gap: '20px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={labelStyle}>Bezug</label>
              <select
                value={bezugValue ?? ''}
                onChange={(e) => onChange(bezugField, parseInt(e.target.value))}
                style={feldStyle}
              >
                {verfuegbar.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            {richtung !== 'am_tag' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={labelStyle}>Werktage</label>
                <input
                  type="number"
                  min="1"
                  value={betrag}
                  onChange={(e) => setzeOffset(parseInt(e.target.value) || 1, richtung)}
                  style={feldStyle}
                />
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={labelStyle}>Richtung</label>
              <select
                value={richtung}
                onChange={(e) => setzeOffset(betrag, e.target.value as 'davor' | 'danach' | 'am_tag')}
                style={feldStyle}
              >
                <option value="am_tag">am Lohnlauf-Tag</option>
                <option value="danach">nach dem Lohnlauf</option>
                <option value="davor">vor dem Lohnlauf</option>
              </select>
            </div>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px' }}>
            {renderField('Tag im Monat', tagField, 'number', undefined, '1-31', tagHelpText)}
            {renderField('Verschiebung bei Wochenende und Feiertagen', verschiebungField, 'select', [
              { value: 'frueher', label: 'Auf den letzten Werktag davor' },
              { value: 'spaeter', label: 'Auf den nächsten Werktag danach' }
            ])}
            {renderField('Monat-Offset', monatOffsetField, 'number', undefined, '0 = aktueller Monat')}
          </div>
        )}
      </>
    );
  };

  // Zwei-Lauf-Modell: Lohnschein-Versand-Konfiguration mit zwei getrennten
  // Lauf-Bloecken (Aktiv-Schalter, fester Tag oder relativ zum jeweiligen Lohnlauf).
  const renderLohnscheinConfigs = () => {
    const configs = lohnscheinConfigs || [];
    const feldStyle = {
      padding: '8px 12px', border: '1px solid #D1D5DB', borderRadius: '6px',
      fontSize: '14px', color: '#111827', backgroundColor: 'white', outline: 'none'
    } as const;
    const labelStyle = { fontSize: '14px', fontWeight: '500', color: '#374151' } as const;

    const renderLauf = (lohnlauf: 1 | 2) => {
      const cfg = configs.find(c => c.lohnlauf === lohnlauf) || defaultLohnscheinLauf(lohnlauf);
      const change = (patch: Partial<LohnscheinLaufFormConfig>) => {
        if (onLohnscheinConfigChange) onLohnscheinConfigChange(lohnlauf, patch);
      };
      const offset = cfg.bezug_werktage_offset ?? 0;
      const betrag = Math.max(1, Math.abs(offset) || 1);
      const richtung: 'davor' | 'danach' | 'am_tag' = offset === 0 ? 'am_tag' : offset < 0 ? 'davor' : 'danach';
      const setzeOffset = (b: number, r: 'davor' | 'danach' | 'am_tag') => {
        if (r === 'am_tag') {
          change({ bezug_werktage_offset: 0 });
          return;
        }
        const v = Math.max(1, b || 1);
        change({ bezug_werktage_offset: r === 'danach' ? v : -v });
      };
      return (
        <div key={lohnlauf} style={{ border: '1px solid #E5E7EB', borderRadius: '12px', padding: '16px 20px', marginBottom: '12px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', marginBottom: cfg.aktiv ? '14px' : '0' }}>
            <input type="checkbox" checked={cfg.aktiv} onChange={(e) => change({ aktiv: e.target.checked })} />
            <span style={{ fontSize: '14px', fontWeight: '500', color: '#111827' }}>{lohnlauf}. Lohnlauf aktiv</span>
          </label>
          {cfg.aktiv && (
            <>
              <div style={{ marginBottom: '14px' }}>
                <div style={{ ...labelStyle, marginBottom: '8px' }}>Fristart</div>
                <div style={{ display: 'flex', gap: '20px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '14px', color: '#374151' }}>
                    <input type="radio" checked={cfg.modus === 'fest'} onChange={() => change({ modus: 'fest', bezug_werktage_offset: null, monat_offset: cfg.monat_offset ?? 0 })} />
                    Fester Tag
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '14px', color: '#374151' }}>
                    <input type="radio" checked={cfg.modus === 'relativ'} onChange={() => change({ modus: 'relativ', bezug_werktage_offset: cfg.bezug_werktage_offset ?? 1, tag: null, verschiebung: null, monat_offset: null })} />
                    Relativ zum {lohnlauf}. Lohnlauf
                  </label>
                </div>
              </div>
              {cfg.modus === 'relativ' ? (
                <div style={{ display: 'grid', gridTemplateColumns: richtung === 'am_tag' ? '1fr' : '1fr 1fr', gap: '16px' }}>
                  {richtung !== 'am_tag' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <label style={labelStyle}>Werktage</label>
                      <input type="number" min="1" value={betrag} onChange={(e) => setzeOffset(parseInt(e.target.value) || 1, richtung)} style={feldStyle} />
                    </div>
                  )}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <label style={labelStyle}>Richtung</label>
                    <select value={richtung} onChange={(e) => setzeOffset(betrag, e.target.value as 'davor' | 'danach' | 'am_tag')} style={feldStyle}>
                      <option value="am_tag">am Lohnlauf-Tag</option>
                      <option value="danach">nach dem Lohnlauf</option>
                      <option value="davor">vor dem Lohnlauf</option>
                    </select>
                  </div>
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <label style={labelStyle}>Tag im Monat</label>
                    <input type="number" min="1" max="31" value={cfg.tag ?? ''} placeholder="1-31" onChange={(e) => change({ tag: e.target.value === '' ? null : parseInt(e.target.value) })} style={feldStyle} />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <label style={labelStyle}>Verschiebung bei Wochenende und Feiertagen</label>
                    <select value={cfg.verschiebung ?? ''} onChange={(e) => change({ verschiebung: e.target.value === '' ? null : e.target.value })} style={feldStyle}>
                      <option value="">Keine</option>
                      <option value="frueher">Auf den letzten Werktag davor</option>
                      <option value="spaeter">Auf den nächsten Werktag danach</option>
                    </select>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <label style={labelStyle}>Monat-Offset</label>
                    <input type="number" value={cfg.monat_offset ?? 0} placeholder="0 = aktueller Monat" onChange={(e) => change({ monat_offset: e.target.value === '' ? 0 : parseInt(e.target.value) })} style={feldStyle} />
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      );
    };

    return (
      <>
        {renderLauf(1)}
        {renderLauf(2)}
      </>
    );
  };

  if (activeTab === 'stammdaten') {
    return (
      <div style={{ 
        display: 'flex',
        flexDirection: 'column',
        gap: '24px'
      }}>
        {/* Hauptdaten */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(2, 1fr)', 
          gap: '20px'
        }}>
          {showContactFields ? (
            /* === MIT Kontaktfeldern (CreateCustomerModal) === */
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {renderField('Firma *', 'company', 'text')}
                {renderField('E-Mail *', 'email', 'email')}
                {renderField('CC-Empfänger', 'email_cc', 'text', undefined, 'z.B. person1@example.com, person2@example.com', 'Mehrere E-Mail-Adressen mit Komma trennen')}
                {renderField('Telefon', 'telefon', 'text', undefined, 'z.B. +49 221 12345678', 'Format: DIN 5008 (z.B. +49 221 12345678 oder 0221 12345678)')}
                {renderField('Vorname *', 'vorname', 'text')}
                {renderField('Nachname *', 'nachname', 'text')}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {renderField('Anrede *', 'anrede', 'select', [
                  { value: 'Sie', label: 'Sie' },
                  { value: 'Du', label: 'Du' }
                ])}
                {renderField('Geschlecht *', 'geschlecht', 'select', [
                  { value: 'männlich', label: 'Männlich' },
                  { value: 'weiblich', label: 'Weiblich' },
                  { value: 'divers', label: 'Divers' }
                ])}
                {renderField('Beraternummer *', 'beraternummer', 'text')}
                {renderField('Mandantennummer *', 'mandantennummer', 'text')}
              </div>
            </>
          ) : (
            /* === OHNE Kontaktfelder (EditCustomerModal) === */
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {renderField('Firma *', 'company', 'text')}
                {renderField('Telefon', 'telefon', 'text', undefined, 'z.B. +49 221 12345678', 'Format: DIN 5008 (z.B. +49 221 12345678 oder 0221 12345678)')}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {renderField('Beraternummer *', 'beraternummer', 'text')}
                {renderField('Mandantennummer *', 'mandantennummer', 'text')}
              </div>
            </>
          )}
        </div>

        {/* Zusätzliche Informationen */}
        <div style={{ 
          paddingTop: '24px', 
          borderTop: '1px solid #E5E7EB' 
        }}>
          <h3 style={{ 
            fontSize: '16px', 
            fontWeight: '600', 
            marginBottom: '20px', 
            color: '#374151',
            margin: '0 0 20px 0'
          }}>
            Zusätzliche Informationen
          </h3>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(2, 1fr)', 
            gap: '20px'
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {renderField('Abrechnungsmethode *', 'abrechnungsmethode', 'select', [
                { value: 'lug', label: 'Lohn und Gehalt (LuG)' },
                { value: 'lodas', label: 'LODAS' }
              ])}
              {/* Auftraggeber-Dropdown: Dynamisch aus DB (Kunden mit ist_auftraggeber = true) */}
              <div style={{ 
                display: 'flex',
                flexDirection: 'column',
                gap: '6px'
              }}>
                <label style={{ 
                  fontSize: '14px', 
                  fontWeight: '500', 
                  color: '#374151' 
                }}>
                  Im Auftrag von
                </label>
                <select
                  value={formData.auftraggeber_id || ''}
                  onChange={(e) => onChange('auftraggeber_id', e.target.value || null)}
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '6px',
                    fontSize: '14px',
                    color: '#111827',
                    backgroundColor: 'white',
                    outline: 'none',
                    transition: 'border-color 0.2s'
                  }}
                >
                  <option value="">-- Kein Auftraggeber --</option>
                  {availableAuftraggeber.map(ag => (
                    <option key={ag.id} value={ag.id}>
                      {ag.company}
                    </option>
                  ))}
                </select>
                <p style={{ 
                  fontSize: '12px', 
                  color: '#6B7280',
                  margin: 0 
                }}>
                  Kunde wird dem Auftraggeber für die Sammelrechnung zugeordnet
                </p>
              </div>
              {/* Kundengruppe (für Umsatzanalyse-Gruppierung) */}
              <div style={{ 
                display: 'flex',
                flexDirection: 'column',
                gap: '6px'
              }}>
                <label style={{ 
                  fontSize: '14px', 
                  fontWeight: '500', 
                  color: '#374151' 
                }}>
                  Kundengruppe (Umsatzanalyse)
                </label>
                <select
                  value={formData.customer_group_id || ''}
                  onChange={(e) => onChange('customer_group_id' as keyof Customer, e.target.value || null)}
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '6px',
                    fontSize: '14px',
                    color: '#111827',
                    backgroundColor: 'white',
                    outline: 'none',
                    transition: 'border-color 0.2s'
                  }}
                >
                  <option value="">-- Keine Gruppe --</option>
                  {availableGroups.map(g => (
                    <option key={g.id} value={g.id}>
                      {g.name}
                    </option>
                  ))}
                </select>
                <p style={{ 
                  fontSize: '12px', 
                  color: '#6B7280',
                  margin: 0 
                }}>
                  Kunden derselben Gruppe werden in der Umsatzanalyse zusammengefasst
                </p>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Zuständiger Mitarbeiter - Custom Dropdown */}
              <div style={{ 
                display: 'flex',
                flexDirection: 'column',
                gap: '6px'
              }}>
                <label style={{ 
                  fontSize: '14px', 
                  fontWeight: '500', 
                  color: '#374151' 
                }}>
                  Zuständiger Mitarbeiter
                </label>
                <select
                  value={formData.zustaendiger_mitarbeiter_id || ''}
                  onChange={(e) => onChange('zustaendiger_mitarbeiter_id', e.target.value || null)}
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '6px',
                    fontSize: '14px',
                    color: '#111827',
                    backgroundColor: 'white',
                    outline: 'none',
                    transition: 'border-color 0.2s'
                  }}
                >
                  <option value="">-- Kein Mitarbeiter zugewiesen --</option>
                  {availableStaff.map(staff => (
                    <option key={staff.id} value={staff.id}>
                      {staff.full_name} ({staff.initials})
                    </option>
                  ))}
                </select>
              </div>
              {/* Duz-Berechtigungen: Personengebundene Du-Anrede beim E-Mail-Versand */}
              <div style={{ 
                display: 'flex',
                flexDirection: 'column',
                gap: '6px'
              }}>
                <label style={{ 
                  fontSize: '14px', 
                  fontWeight: '500', 
                  color: '#374151' 
                }}>
                  Diese Mitarbeiter duzen den Kunden
                </label>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                  padding: '12px 16px',
                  backgroundColor: '#F9FAFB',
                  borderRadius: '6px',
                  border: '1px solid #E5E7EB'
                }}>
                  {availableStaff.map(staff => {
                    const selected = (formData.duz_mitarbeiter_ids || []).includes(staff.id);
                    return (
                      <label
                        key={staff.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          cursor: 'pointer',
                          userSelect: 'none'
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={(e) => {
                            const current = formData.duz_mitarbeiter_ids || [];
                            const next = e.target.checked
                              ? [...current, staff.id]
                              : current.filter(id => id !== staff.id);
                            onChange('duz_mitarbeiter_ids', next);
                          }}
                          style={{
                            width: '16px',
                            height: '16px',
                            cursor: 'pointer'
                          }}
                        />
                        <span style={{ fontSize: '14px', color: '#374151' }}>
                          {staff.full_name} ({staff.initials})
                        </span>
                      </label>
                    );
                  })}
                </div>
                <p style={{ 
                  fontSize: '12px', 
                  color: '#6B7280',
                  margin: 0 
                }}>
                  Die Du-Anrede in E-Mails greift nur, wenn der Absender hier ausgewählt ist — andere Absender siezen automatisch.
                </p>
              </div>
              {renderField('Zendesk-Tag', 'zendesk_tag', 'text')}
            </div>
          </div>
        </div>

        {/* Versand-Einstellungen (verschoben von Steuer & Versand) */}
        <div style={{ 
          paddingTop: '24px', 
          borderTop: '1px solid #E5E7EB' 
        }}>
          <h3 style={{ 
            fontSize: '16px', 
            fontWeight: '600', 
            marginBottom: '16px', 
            color: '#374151',
            margin: '0 0 16px 0'
          }}>
            Versand-Einstellungen
          </h3>
          <div style={{ 
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            maxWidth: '400px'
          }}>
            {renderField('Versandmethode *', 'versandmethode', 'select', [
              { value: 'email', label: 'E-Mail' },
              { value: 'duo', label: 'DATEV Unternehmen Online (DUO)' },
              // xtax_portal nur sichtbar bei DATEV-Anbindung; bleibt sichtbar
              // wenn aktuell ausgewählt (damit der User weg-wählen kann, falls
              // die DATEV-Felder nachträglich entfernt wurden)
              ...((formData.beraternummer && formData.mandantennummer) || formData.versandmethode === 'xtax_portal'
                ? [{ value: 'xtax_portal', label: 'XTAX-Portal (DATEV-automatisch)' }]
                : []
              ),
              { value: 'kein_versand', label: 'Kein Versand' }
            ])}
            <div style={{
              padding: '12px 16px',
              backgroundColor: '#F9FAFB',
              borderRadius: '6px',
              border: '1px solid #E5E7EB'
            }}>
              {renderField('Postalischer Versand', 'postalischer_versand', 'checkbox')}
            </div>
          </div>

          {/* Conditional Info Boxes */}
          {formData.versandmethode === 'duo' && (
            <div style={{
              padding: '12px 16px',
              backgroundColor: '#F0F8FF',
              borderRadius: '6px',
              border: '1px solid #B8E0FF',
              marginTop: '16px'
            }}>
              <p style={{ 
                fontSize: '13px', 
                color: '#0066CC', 
                margin: 0,
                lineHeight: '1.5'
              }}>
                Bei DUO erhält der Kunde eine E-Mail-Benachrichtigung ohne PDF-Anhang. 
                Die Dokumente werden direkt in DATEV Unternehmen Online bereitgestellt.
              </p>
            </div>
          )}

          {formData.versandmethode === 'xtax_portal' && (
            <div style={{
              padding: '12px 16px',
              backgroundColor: '#EFF6FF',
              borderRadius: '6px',
              border: '1px solid #BFDBFE',
              marginTop: '16px'
            }}>
              <p style={{
                fontSize: '13px',
                color: '#1D4ED8',
                margin: 0,
                lineHeight: '1.5'
              }}>
                <strong>XTAX-Portal:</strong> Sobald die Auswertungen aus DATEV vorliegen,
                werden sie automatisch im Mandantenportal bereitgestellt und der für die
                Finalisierung berechtigte Ansprechpartner per E-Mail benachrichtigt.
                Es ist keine manuelle PDF-Auswahl bei der Finalisierung nötig.
              </p>
              {(!formData.beraternummer || !formData.mandantennummer) && (
                <p style={{
                  fontSize: '13px',
                  color: '#991B1B',
                  margin: '8px 0 0 0',
                  lineHeight: '1.5',
                  fontWeight: '500'
                }}>
                  Achtung: Berater- und Mandantennummer müssen gesetzt sein, damit diese
                  Versandart funktioniert. Bitte ergänzen Sie die DATEV-Felder oder wählen
                  Sie eine andere Versandart.
                </p>
              )}
            </div>
          )}

          {formData.postalischer_versand && (
            <div style={{
              padding: '12px 16px',
              backgroundColor: '#EEF2FF',
              borderRadius: '6px',
              border: '1px solid #C7D2FE',
              marginTop: '16px'
            }}>
              <p style={{ 
                fontSize: '13px', 
                color: '#4338CA', 
                margin: 0,
                lineHeight: '1.5'
              }}>
                <strong>Postalischer Versand aktiviert:</strong> DATEV versendet die Auswertungen 
                zusätzlich per Postbrief an den Kunden.
              </p>
            </div>
          )}
        </div>

        {/* Gesicherter Dokumentenversand - NUR bei Kontaktfeldern (CreateCustomerModal) */}
        {showContactFields && (
          <div style={{ 
            paddingTop: '24px', 
            borderTop: '1px solid #E5E7EB' 
          }}>
            <h3 style={{ 
              fontSize: '16px', 
              fontWeight: '600', 
              marginBottom: '16px', 
              color: '#374151',
              margin: '0 0 16px 0'
            }}>
              Gesicherter Dokumentenversand
            </h3>
            <div style={{ 
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              maxWidth: '400px'
            }}>
              {renderField(
                'Download-Passwort', 
                'secure_download_passwort', 
                'text', 
                undefined, 
                'z.B. Passwort123', 
                'Passwort für den gesicherten Kunden-Downloadbereich'
              )}
            </div>
            <div style={{
              padding: '12px 16px',
              backgroundColor: '#FEF3C7',
              borderRadius: '6px',
              border: '1px solid #FDE68A',
              marginTop: '16px',
              maxWidth: '400px'
            }}>
              <p style={{ 
                fontSize: '13px', 
                color: '#92400E', 
                margin: 0,
                lineHeight: '1.5'
              }}>
                <strong>Hinweis:</strong> Dieses Passwort wird benötigt, um dem Kunden Dokumente 
                über den gesicherten Downloadbereich bereitzustellen. Das Passwort muss dem 
                Kunden separat mitgeteilt werden.
              </p>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (activeTab === 'lohnlauf') {
    return (
      <div style={{ 
        display: 'flex',
        flexDirection: 'column',
        gap: '32px'
      }}>
        {/* Erster Lohnlauf */}
        <div>
          <h3 style={{ 
            fontSize: '16px', 
            fontWeight: '600', 
            marginBottom: '16px', 
            color: '#374151',
            margin: '0 0 16px 0'
          }}>
            Erster Lohnlauf
          </h3>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(3, 1fr)', 
            gap: '20px'
          }}>
            {renderField('Tag im Monat *', 'erster_lohnlauf_tag', 'number', undefined, '1-31')}
            {renderField('Verschiebung bei Wochenende und Feiertagen', 'erster_lohnlauf_verschiebung', 'select', [
              { value: 'frueher', label: 'Auf den letzten Werktag davor' },
              { value: 'spaeter', label: 'Auf den nächsten Werktag danach' }
            ])}
            {renderField('Monat-Offset', 'erster_lohnlauf_monat_offset', 'number', undefined, '0 = aktueller Monat')}
          </div>
          {/* FRIST AB ANLIEFERUNG: Toggle für den ersten Lohnlauf */}
          <div style={{
            padding: '12px 16px',
            backgroundColor: '#F9FAFB',
            borderRadius: '6px',
            border: '1px solid #E5E7EB',
            marginTop: '16px',
            maxWidth: '600px'
          }}>
            {renderField('Frist läuft ab Datenanlieferung', 'erster_lohnlauf_frist_ab_anlieferung', 'checkbox')}
            <p style={{
              fontSize: '12px',
              color: '#6B7280',
              margin: '4px 0 0 32px',
              lineHeight: '1.4'
            }}>
              Wenn aktiv, ist der oben konfigurierte Tag nur der Erinnerungstermin, falls der Kunde keine Daten schickt. Sobald im Fristenboard &bdquo;Daten angekommen&ldquo; markiert wird, gilt als Frist der n&auml;chste Werktag nach dem Anlieferungstag (Wochenenden und NRW-Feiertage werden ber&uuml;cksichtigt).
            </p>
          </div>
        </div>

        {/* Zweiter Lohnlauf */}
        <div>
          <h3 style={{ 
            fontSize: '16px', 
            fontWeight: '600', 
            marginBottom: '16px', 
            color: '#374151',
            margin: '0 0 16px 0'
          }}>
            Zweiter Lohnlauf (optional)
          </h3>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(3, 1fr)', 
            gap: '20px'
          }}>
            {renderField('Tag im Monat', 'zweiter_lohnlauf_tag', 'number', undefined, '1-31')}
            {renderField('Verschiebung bei Wochenende und Feiertagen', 'zweiter_lohnlauf_verschiebung', 'select', [
              { value: 'frueher', label: 'Auf den letzten Werktag davor' },
              { value: 'spaeter', label: 'Auf den nächsten Werktag danach' }
            ])}
            {renderField('Monat-Offset', 'zweiter_lohnlauf_monat_offset', 'number', undefined, '0 = aktueller Monat')}
          </div>
          {/* FRIST AB ANLIEFERUNG: Toggle für den zweiten Lohnlauf */}
          <div style={{
            padding: '12px 16px',
            backgroundColor: '#F9FAFB',
            borderRadius: '6px',
            border: '1px solid #E5E7EB',
            marginTop: '16px',
            maxWidth: '600px'
          }}>
            {renderField('Frist läuft ab Datenanlieferung', 'zweiter_lohnlauf_frist_ab_anlieferung', 'checkbox')}
            <p style={{
              fontSize: '12px',
              color: '#6B7280',
              margin: '4px 0 0 32px',
              lineHeight: '1.4'
            }}>
              Wenn aktiv, ist der oben konfigurierte Tag nur der Erinnerungstermin, falls der Kunde keine Daten schickt. Sobald im Fristenboard &bdquo;Daten angekommen&ldquo; markiert wird, gilt als Frist der n&auml;chste Werktag nach dem Anlieferungstag (Wochenenden und NRW-Feiertage werden ber&uuml;cksichtigt).
            </p>
          </div>
        </div>

        {/* Lohnschein-Versand an Arbeitnehmer (Stufe 3.2) */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
            <h3 style={{ 
              fontSize: '16px', 
              fontWeight: '600', 
              color: '#374151',
              margin: 0
            }}>
              Lohnschein-Versand an Arbeitnehmer (optional)
            </h3>
            <InfoTooltip label="Info zum Lohnschein-Versand" text={
              <>
                <strong>Lohnschein-Versand:</strong> Ist ein Tag gesetzt, erscheint im Fristenkalender
                die Frist &bdquo;Lohnschein-Versand&ldquo;. Beim Erledigen der Frist werden die
                Lohndokumente (Brutto/Netto-Abrechnung, Lohnsteuerbescheinigung, SV-Nachweis) je
                Arbeitnehmer zu einem PDF gebündelt und mit der bekannten E-Mail-Vorlage direkt an
                die Arbeitnehmer versendet. Arbeitnehmer ohne E-Mail-Adresse oder ohne IBAN im
                DATEV-Spiegel erhalten keinen Direktversand &ndash; deren Bündel gehen als
                Sammelpaket an den Ansprechpartner mit der Option &bdquo;Erhält Lohnschein-Bericht&ldquo;.
                Voraussetzung: DATEV-Anbindung mit Mitarbeiter-Spiegel (DATEV Lohnaustauschdatenservice).
              </>
            } />
          </div>
          {renderLohnscheinConfigs()}
          <div style={{ marginTop: '16px' }}>
            {renderField('Lohnschein-Versand ohne Passwortschutz', 'lohnschein_ohne_passwort', 'checkbox')}
            <p style={{ fontSize: '13px', color: '#6B7280', margin: '4px 0 0 32px', lineHeight: '1.5' }}>
              Nur für Firmen ohne IBAN-Erfassung: Die Lohnscheine werden ohne Passwort direkt an die
              Arbeitnehmer versendet. Ohne diese Option ist das Passwort die letzten vier Ziffern der IBAN.
            </p>
          </div>
          {(!formData.beraternummer || !formData.mandantennummer) && (lohnscheinConfigs || []).some(c => c.aktiv) && (
            <div style={{
              padding: '12px 16px',
              backgroundColor: '#FEF2F2',
              borderRadius: '6px',
              border: '1px solid #FECACA',
              marginTop: '16px'
            }}>
              <p style={{
                fontSize: '13px',
                color: '#991B1B',
                margin: 0,
                lineHeight: '1.5',
                fontWeight: '500'
              }}>
                Achtung: Berater- und Mandantennummer müssen gesetzt sein, damit der
                Lohnschein-Versand funktioniert. Bitte ergänzen Sie die DATEV-Felder.
              </p>
            </div>
          )}
        </div>

        {/* OPPM Gehaltsdokumente mit QR-Code (nur fuer Standorte der Gruppe OPPM) */}
        {isOppmStandort && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
            <h3 style={{ 
              fontSize: '16px', 
              fontWeight: '600', 
              color: '#374151',
              margin: 0
            }}>
              OPPM Gehaltsdokumente mit QR-Code (optional)
            </h3>
            <InfoTooltip label="Info zum OPPM QR-Code-Versand" text={
              <>
                <strong>OPPM QR-Code-Versand:</strong> Ist ein Tag gesetzt, erscheint im Fristenkalender
                die Frist &bdquo;OPPM QR-Code-Versand&ldquo;. Beim Erledigen der Frist werden die
                Gehaltsdokumente (Brutto/Netto-Abrechnung, Lohnsteuerbescheinigung, SV-Meldungen) des
                Vormonats je Mitarbeiter mit einem QR-Code bestempelt, als ZIP im geschützten
                Downloadbereich bereitgestellt und der ausgewählte Ansprechpartner benachrichtigt.
                Voraussetzung: DATEV-Anbindung.
              </>
            } />
          </div>
          {renderSpezialfristFelder('oppm_qr_versand', 'Leer lassen = kein OPPM-QR-Versand')}
          {(!formData.beraternummer || !formData.mandantennummer) && (!!formData.oppm_qr_versand_tag || !!formData.oppm_qr_versand_bezug_lohnlauf) && (
            <div style={{
              padding: '12px 16px',
              backgroundColor: '#FEF2F2',
              borderRadius: '6px',
              border: '1px solid #FECACA',
              marginTop: '16px'
            }}>
              <p style={{
                fontSize: '13px',
                color: '#991B1B',
                margin: 0,
                lineHeight: '1.5',
                fontWeight: '500'
              }}>
                Achtung: Berater- und Mandantennummer müssen gesetzt sein, damit der
                OPPM-QR-Versand funktioniert. Bitte ergänzen Sie die DATEV-Felder.
              </p>
            </div>
          )}
        </div>
        )}

        {/* OPPM Monatsübersichten mit QR-Code (nur fuer Standorte der Gruppe OPPM) */}
        {isOppmStandort && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
            <h3 style={{ 
              fontSize: '16px', 
              fontWeight: '600', 
              color: '#374151',
              margin: 0
            }}>
              OPPM Monatsübersichten mit QR-Code (optional)
            </h3>
            <InfoTooltip label="Info zum OPPM Monatsübersicht-Versand" text={
              <>
                <strong>OPPM Monatsübersicht-Versand:</strong> Ist ein Tag gesetzt, erscheint im
                Fristenkalender die Frist &bdquo;OPPM Monatsübersicht-Versand&ldquo;. Beim Erledigen
                werden die Monatsübersichten des Vormonats je Mitarbeiter mit einem QR-Code bestempelt,
                als ZIP im geschützten Downloadbereich bereitgestellt und der ausgewählte Ansprechpartner
                benachrichtigt, sobald der externe Lauf die Dateien vollständig bereitgestellt hat.
                Voraussetzung: DATEV-Anbindung.
              </>
            } />
          </div>
          {renderSpezialfristFelder('monatsuebersicht_qr_versand', 'Leer lassen = kein Monatsübersicht-Versand')}
          {(!formData.beraternummer || !formData.mandantennummer) && (!!formData.monatsuebersicht_qr_versand_tag || !!formData.monatsuebersicht_qr_versand_bezug_lohnlauf) && (
            <div style={{
              padding: '12px 16px',
              backgroundColor: '#FEF2F2',
              borderRadius: '6px',
              border: '1px solid #FECACA',
              marginTop: '16px'
            }}>
              <p style={{
                fontSize: '13px',
                color: '#991B1B',
                margin: 0,
                lineHeight: '1.5',
                fontWeight: '500'
              }}>
                Achtung: Berater- und Mandantennummer müssen gesetzt sein, damit der
                Monatsübersicht-Versand funktioniert. Bitte ergänzen Sie die DATEV-Felder.
              </p>
            </div>
          )}
        </div>
        )}

        {/* Hinweis zum Monat-Offset als Tooltip */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '13px', fontWeight: '500', color: '#374151' }}>
            Hinweis zum Monat-Offset
          </span>
          <InfoTooltip label="Hinweis zum Monat-Offset" text={
            <>
              <strong>Hinweis zum Monat-Offset:</strong> Der Offset verschiebt den Abrechnungszeitpunkt relativ zum Abrechnungsmonat.
              Beispiel: Bei Offset 1 wird die Abrechnung für Januar erst im Februar durchgeführt. Bei Offset -1 wird die Abrechnung für Februar bereits im Januar durchgeführt.
            </>
          } />
        </div>
      </div>
    );
  }

  if (activeTab === 'steuer_sozialversicherung') {
    return (
      <div style={{ 
        display: 'flex',
        flexDirection: 'column',
        gap: '32px'
      }}>
        {/* Steuer-Einstellungen */}
        <div>
          <h3 style={{ 
            fontSize: '16px', 
            fontWeight: '600', 
            marginBottom: '16px', 
            color: '#374151',
            margin: '0 0 16px 0'
          }}>
            Lohnsteuer
          </h3>
          <div style={{ 
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            maxWidth: '400px'
          }}>
            {renderField('Lohnsteuer-Zeitraum *', 'lohnsteuer_zeitraum', 'select', [
              { value: 'monatlich', label: 'Monatlich' },
              { value: 'monatlich_versetzt', label: 'Monatlich (versetzt)' },
              { value: 'vierteljaehrlich', label: 'Vierteljährlich' },
              { value: 'jaehrlich', label: 'Jährlich' },
              { value: 'keine', label: 'Keine' }
            ])}
            {renderField(
              'Steuernummer (Lohnsteuer)',
              'lohnsteuer_steuernummer',
              'text',
              undefined,
              'z.B. 105/5780/1234',
              'Steuernummer des Arbeitgebers für die Lohnsteuer. Erscheint in Steuer-ID-Anträgen als Steuernummer des Arbeitgebers.'
            )}
            {renderField(
              'Betriebsstättenfinanzamt',
              'finanzamt_id',
              'select',
              availableFinanzaemter.map(fa => ({ value: fa.id, label: fa.name })),
              undefined,
              'Empfänger der Steuer-ID-Anträge für Mitarbeiter dieses Kunden.'
            )}
          </div>
        </div>

        {/* Sozialversicherung */}
        <div>
          <h3 style={{ 
            fontSize: '16px', 
            fontWeight: '600', 
            marginBottom: '16px', 
            color: '#374151',
            margin: '0 0 16px 0'
          }}>
            Sozialversicherung
          </h3>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            padding: '12px 16px',
            backgroundColor: '#F9FAFB',
            borderRadius: '6px',
            border: '1px solid #E5E7EB',
            maxWidth: '400px'
          }}>
            {renderField('SV-Pflicht', 'sv_pflicht', 'checkbox')}
            {renderField('Schätzmandat', 'schaetzmandat', 'checkbox')}
          </div>
        </div>
      </div>
    );
  }

  if (activeTab === 'rechnungsdaten') {
    return (
      <div style={{ 
        display: 'flex',
        flexDirection: 'column',
        gap: '32px'
      }}>
        {/* Rechnungsadresse */}
        <div>
          <h3 style={{ 
            fontSize: '16px', 
            fontWeight: '600', 
            marginBottom: '16px', 
            color: '#374151',
            margin: '0 0 16px 0'
          }}>
            Rechnungsadresse
          </h3>
          <div style={{ marginBottom: '20px' }}>
            {renderField(
              'Firmenname (abweichend für Rechnung)',
              'company_rechnung',
              'text',
              undefined,
              formData.company || 'z.B. Muster GmbH & Co. KG',
              'Leer lassen, wenn identisch mit Firmenname. Erscheint als Empfänger auf der Rechnung.'
            )}
          </div>
          {/* Abweichender Rechnungsempfänger (Person im Adressfeld der Rechnung) */}
          <div style={{ marginBottom: '20px' }}>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(3, 1fr)', 
              gap: '20px'
            }}>
              {renderField('Anrede (abweichender Rechnungsempfänger)', 'rechnung_empfaenger_anrede', 'select', [
                { value: 'Herr', label: 'Herr' },
                { value: 'Frau', label: 'Frau' }
              ])}
              {renderField('Vorname (abweichender Rechnungsempfänger)', 'rechnung_empfaenger_vorname', 'text', undefined, 'z.B. Tarek')}
              {renderField('Nachname (abweichender Rechnungsempfänger)', 'rechnung_empfaenger_nachname', 'text', undefined, 'z.B. Hussain')}
            </div>
            <p style={{
              fontSize: '12px',
              color: '#6B7280',
              margin: '8px 0 0 0',
              lineHeight: '1.4'
            }}>
              Leer lassen, wenn die Rechnung an den Ansprechpartner adressiert wird. Ist ein Nachname gesetzt, erscheint diese Person im Adressfeld der Rechnung und in der Anrede von Rechnungs- und Erinnerungsmail. Die Anrede ist dann Pflicht, sonst wird die Namenszeile auf der Rechnung nicht gedruckt.
            </p>
          </div>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(2, 1fr)', 
            gap: '20px'
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {renderField('Straße', 'strasse', 'text', undefined, 'z.B. Hansaring 78')}
              {renderField('PLZ', 'plz', 'text', undefined, 'z.B. 50670')}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {renderField('Ort', 'ort', 'text', undefined, 'z.B. Köln')}
              {renderField('Land', 'land', 'text', undefined, 'z.B. Deutschland')}
            </div>
          </div>
          <div style={{
            marginTop: '20px',
            padding: '12px 16px',
            backgroundColor: '#F9FAFB',
            borderRadius: '6px',
            border: '1px solid #E5E7EB',
            maxWidth: '600px'
          }}>
            {renderField('Anonyme Anrede in Rechnungsmail', 'rechnung_anonyme_anrede', 'checkbox')}
            <p style={{
              fontSize: '12px',
              color: '#6B7280',
              margin: '4px 0 0 32px',
              lineHeight: '1.4'
            }}>
              Wenn aktiv, nutzt die Rechnungsmail &bdquo;Sehr geehrte Damen und Herren&ldquo; und die Sie-Form &ndash; unabh&auml;ngig vom Ansprechpartner am Kunden.
            </p>
          </div>
        </div>

        {/* Rechnungskonfiguration */}
        <div style={{ 
          paddingTop: '24px', 
          borderTop: '1px solid #E5E7EB' 
        }}>
          <h3 style={{ 
            fontSize: '16px', 
            fontWeight: '600', 
            marginBottom: '16px', 
            color: '#374151',
            margin: '0 0 16px 0'
          }}>
            Rechnungskonfiguration
          </h3>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(2, 1fr)', 
            gap: '20px'
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {renderField('Kundennummer (Rechnung)', 'kundennummer_rechnung', 'text', undefined, 'z.B. K-10001', 'Wird auf der Rechnung als "Ihre Kundennummer" angezeigt')}
              {renderField('Rechnungs-E-Mail', 'rechnungs_email', 'email', undefined, 'z.B. buchhaltung@firma.de', 'Abweichende E-Mail für den Rechnungsversand')}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Preis pro Arbeitnehmer - custom Input mit Komma-Unterstützung (nicht in customers-Tabelle) */}
              <div style={{ 
                display: 'flex',
                flexDirection: 'column',
                gap: '6px'
              }}>
                <label style={{ 
                  fontSize: '14px', 
                  fontWeight: '500', 
                  color: '#374151' 
                }}>
                  Preis pro Arbeitnehmer (€)
                </label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={pricingDisplay}
                  onChange={(e) => {
                    const raw = e.target.value;
                    // Nur Ziffern, Komma und Punkt erlauben
                    if (raw !== '' && !/^[\d.,]*$/.test(raw)) return;
                    updatePricingDisplay(raw);
                    // Komma → Punkt für parseFloat
                    const normalized = raw.replace(',', '.');
                    const parsed = parseFloat(normalized);
                    if (onPricingChange) {
                      onPricingChange(raw === '' ? null : (isNaN(parsed) ? null : parsed));
                    }
                  }}
                  placeholder="z.B. 11,78"
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '6px',
                    fontSize: '14px',
                    color: '#111827',
                    backgroundColor: 'white',
                    outline: 'none',
                    transition: 'border-color 0.2s'
                  }}
                />
                <p style={{ 
                  fontSize: '12px', 
                  color: '#6B7280',
                  margin: 0 
                }}>
                  Stückpreis für die Rechnungsposition "Lohnabrechnungen"
                </p>
              </div>
              {/* Stundensatz - custom Input mit Komma-Unterstützung (Tabelle customer_stundensatz) */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '6px'
              }}>
                <label style={{
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151'
                }}>
                  Stundensatz (€)
                </label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={stundensatzDisplay}
                  onChange={(e) => {
                    const raw = e.target.value;
                    // Nur Ziffern, Komma und Punkt erlauben
                    if (raw !== '' && !/^[\d.,]*$/.test(raw)) return;
                    updateStundensatzDisplay(raw);
                    // Komma → Punkt für parseFloat
                    const normalized = raw.replace(',', '.');
                    const parsed = parseFloat(normalized);
                    if (onStundensatzChange) {
                      onStundensatzChange(raw === '' ? null : (isNaN(parsed) ? null : parsed));
                    }
                  }}
                  placeholder="z.B. 45,00"
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '6px',
                    fontSize: '14px',
                    color: '#111827',
                    backgroundColor: 'white',
                    outline: 'none',
                    transition: 'border-color 0.2s'
                  }}
                />
                <p style={{
                  fontSize: '12px',
                  color: '#6B7280',
                  margin: 0
                }}>
                  Standard-Satz für Zusatzleistungen (Stunden). Pro Eintrag überschreibbar.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Vertragsdaten - nur fuer Direktkunden (kein Auftraggeber gesetzt) */}
        {!formData.auftraggeber_id && (
          <div style={{ 
            paddingTop: '24px', 
            borderTop: '1px solid #E5E7EB' 
          }}>
            <h3 style={{ 
              fontSize: '16px', 
              fontWeight: '600', 
              marginBottom: '16px', 
              color: '#374151',
              margin: '0 0 16px 0'
            }}>
              Vertragsdaten
            </h3>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(2, 1fr)', 
              gap: '20px'
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {/* Beginn der Zusammenarbeit - Monatsauswahl, gespeichert auf den Monatsersten */}
                <div style={{ 
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '6px'
                }}>
                  <label style={{ 
                    fontSize: '14px', 
                    fontWeight: '500', 
                    color: '#374151' 
                  }}>
                    Beginn der Zusammenarbeit
                  </label>
                  <input
                    type="month"
                    value={(formData.zusammenarbeit_beginn || '').slice(0, 7)}
                    onChange={(e) => onChange('zusammenarbeit_beginn', e.target.value ? `${e.target.value}-01` : null)}
                    style={{
                      padding: '8px 12px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '6px',
                      fontSize: '14px',
                      color: '#111827',
                      backgroundColor: 'white',
                      outline: 'none',
                      transition: 'border-color 0.2s'
                    }}
                  />
                  <p style={{ 
                    fontSize: '12px', 
                    color: '#6B7280',
                    margin: 0 
                  }}>
                    Monat, in dem die Zusammenarbeit begonnen hat
                  </p>
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {/* Vertragsschluss - tagesgenaues Datum (loest die AGB-Frist aus) */}
                <div style={{ 
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '6px'
                }}>
                  <label style={{ 
                    fontSize: '14px', 
                    fontWeight: '500', 
                    color: '#374151' 
                  }}>
                    Vertragsschluss
                  </label>
                  <input
                    type="date"
                    value={formData.vertragsschluss || ''}
                    onChange={(e) => onChange('vertragsschluss', e.target.value || null)}
                    style={{
                      padding: '8px 12px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '6px',
                      fontSize: '14px',
                      color: '#111827',
                      backgroundColor: 'white',
                      outline: 'none',
                      transition: 'border-color 0.2s'
                    }}
                  />
                  <p style={{ 
                    fontSize: '12px', 
                    color: '#6B7280',
                    margin: 0 
                  }}>
                    Datum des Vertragsschlusses. Maßgeblich für die erste mögliche Preisanpassung laut AGB.
                  </p>
                </div>
              </div>
            </div>
            {/* Vertrag-Upload - eigenstaendige Sofortaktion, getrennt vom Speichern */}
            {customerId && (
              <div style={{ marginTop: '20px', maxWidth: '600px' }}>
                <ContractUpload customerId={customerId} />
              </div>
            )}
            {/* Digitale Vertraege - Erzeugung, Vorschau und Historie.
                Ergaenzt den manuellen Upload, ersetzt ihn nicht. */}
            {customerId && (
              <VertragPanel customerId={customerId} />
            )}
          </div>
        )}

        {/* Zahlungsart (Feature 2: Lastschrift) */}
        <div style={{ 
          paddingTop: '24px', 
          borderTop: '1px solid #E5E7EB' 
        }}>
          <h3 style={{ 
            fontSize: '16px', 
            fontWeight: '600', 
            marginBottom: '16px', 
            color: '#374151',
            margin: '0 0 16px 0'
          }}>
            Zahlungsart
          </h3>
          <div style={{ 
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            maxWidth: '400px'
          }}>
            {renderField('Zahlungsart', 'zahlungsart', 'select', [
              { value: 'ueberweisung', label: 'Überweisung' },
              { value: 'lastschrift', label: 'SEPA-Lastschrift' }
            ])}
          </div>

          {/* SEPA-Lastschrift-Felder (nur sichtbar wenn Lastschrift gewählt) */}
          {formData.zahlungsart === 'lastschrift' && (
            <div style={{ 
              marginTop: '20px',
              padding: '20px',
              backgroundColor: '#F9FAFB',
              borderRadius: '8px',
              border: '1px solid #E5E7EB'
            }}>
              <h4 style={{ 
                fontSize: '14px', 
                fontWeight: '600', 
                marginBottom: '16px', 
                color: '#374151',
                margin: '0 0 16px 0'
              }}>
                SEPA-Lastschrift-Daten
              </h4>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(2, 1fr)', 
                gap: '16px'
              }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {renderField('IBAN *', 'lastschrift_iban', 'text', undefined, 'z.B. DE89 3704 0044 0532 0130 00')}
                  {renderField('BIC', 'lastschrift_bic', 'text', undefined, 'z.B. COBADEFFXXX')}
                  {renderField('Kontoinhaber', 'lastschrift_kontoinhaber', 'text', undefined, 'Falls abweichend vom Firmennamen')}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {renderField('Mandatsreferenz *', 'lastschrift_mandatsreferenz', 'text', undefined, 'z.B. XTAX-2026-001')}
                  {renderField('Mandatsdatum *', 'lastschrift_mandatsdatum', 'text', undefined, 'z.B. 2026-01-15', 'Datum der Mandatserteilung (JJJJ-MM-TT)')}
                  {renderField('Mandatsart', 'lastschrift_mandatsart', 'select', [
                    { value: 'SEPA-Basislastschrift', label: 'SEPA-Basislastschrift' },
                    { value: 'SEPA-Firmenlastschrift', label: 'SEPA-Firmenlastschrift' }
                  ])}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Reverse Charge (nur bei Auslandskunden) */}
        {formData.land && formData.land !== 'Deutschland' && (
          <div style={{ 
            paddingTop: '24px', 
            borderTop: '1px solid #E5E7EB' 
          }}>
            <h3 style={{ 
              fontSize: '16px', 
              fontWeight: '600', 
              marginBottom: '16px', 
              color: '#374151',
              margin: '0 0 16px 0'
            }}>
              Umsatzsteuer (Ausland)
            </h3>
            <div style={{ 
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              maxWidth: '400px'
            }}>
              {renderField('Reverse Charge Verfahren', 'reverse_charge', 'checkbox')}
            </div>

            {/* USt-IdNr Feld (nur sichtbar wenn Reverse Charge aktiv) */}
            {formData.reverse_charge && (
              <div style={{ 
                marginTop: '20px',
                padding: '20px',
                backgroundColor: '#F9FAFB',
                borderRadius: '8px',
                border: '1px solid #E5E7EB'
              }}>
                <p style={{ 
                  fontSize: '13px', 
                  color: '#92400E', 
                  margin: '0 0 16px 0',
                  padding: '8px 12px',
                  backgroundColor: '#FFFBEB',
                  borderRadius: '6px',
                  border: '1px solid #FDE68A',
                  lineHeight: '1.5'
                }}>
                  <strong>Reverse Charge aktiv:</strong> Rechnungen werden mit 0% USt erstellt. 
                  Auf der Rechnung erscheint der Hinweis zur Steuerschuldnerschaft des Leistungsempfängers 
                  (Art. 196 Richtlinie 2006/112/EG).
                </p>
                <div style={{ maxWidth: '400px' }}>
                  {renderField('USt-IdNr des Kunden *', 'ust_id_kunde', 'text', undefined, 'z.B. HU13567116 oder LT100007431118', 'Pflichtfeld bei Reverse Charge')}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Info-Hinweis */}
        <div style={{
          padding: '12px 16px',
          backgroundColor: '#EFF6FF',
          borderRadius: '6px',
          border: '1px solid #BFDBFE'
        }}>
          <p style={{ 
            fontSize: '13px', 
            color: '#1E40AF', 
            margin: 0,
            lineHeight: '1.5'
          }}>
            <strong>Hinweis:</strong> Diese Daten werden für die automatische Rechnungserstellung verwendet. 
            Die Rechnungsadresse erscheint als Empfängeradresse auf der PDF-Rechnung. 
            Der Preis pro Arbeitnehmer wird mit der Anzahl der abgerechneten Mitarbeiter multipliziert.
          </p>
        </div>
      </div>
    );
  }

  return null;
};

export default CustomerForm;
